import React, { useState, useEffect } from 'react';
import { clientAPI, applicationAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Plus,
  Loader2,
  Building2,
  AppWindow,
  Edit,
  Trash2,
  ChevronRight,
  X
} from 'lucide-react';

const Clients = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [applications, setApplications] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [appDialogOpen, setAppDialogOpen] = useState(false);
  const [newDept, setNewDept] = useState('');
  
  const [clientForm, setClientForm] = useState({
    name: '',
    code: '',
    logo_url: '',
    departments: ['Finance', 'Sales', 'IT', 'HR', 'Management', 'Operations']
  });
  
  const [appForm, setAppForm] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      fetchApplications(selectedClient.id);
    }
  }, [selectedClient]);

  const fetchClients = async () => {
    try {
      const response = await clientAPI.getClients();
      setClients(response.data);
      if (response.data.length > 0 && !selectedClient) {
        setSelectedClient(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async (clientId) => {
    try {
      const response = await applicationAPI.getApplications({ client_id: clientId });
      setApplications(response.data);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    }
  };

  const handleCreateClient = async () => {
    if (!clientForm.name || !clientForm.code) return;
    try {
      await clientAPI.createClient(clientForm);
      setClientDialogOpen(false);
      resetClientForm();
      fetchClients();
    } catch (error) {
      console.error('Failed to create client:', error);
      alert(error.response?.data?.detail || 'Failed to create client');
    }
  };

  const handleCreateApp = async () => {
    if (!appForm.name || !selectedClient) return;
    try {
      await applicationAPI.createApplication({
        ...appForm,
        client_id: selectedClient.id
      });
      setAppDialogOpen(false);
      resetAppForm();
      fetchApplications(selectedClient.id);
    } catch (error) {
      console.error('Failed to create application:', error);
      alert(error.response?.data?.detail || 'Failed to create application');
    }
  };

  const handleDeleteApp = async (appId) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    try {
      await applicationAPI.deleteApplication(appId);
      fetchApplications(selectedClient.id);
    } catch (error) {
      console.error('Failed to delete application:', error);
    }
  };

  const resetClientForm = () => {
    setClientForm({
      name: '',
      code: '',
      logo_url: '',
      departments: ['Finance', 'Sales', 'IT', 'HR', 'Management', 'Operations']
    });
  };

  const resetAppForm = () => {
    setAppForm({ name: '', description: '' });
  };

  const addDepartment = () => {
    if (!newDept.trim() || clientForm.departments.includes(newDept.trim())) return;
    setClientForm(prev => ({
      ...prev,
      departments: [...prev.departments, newDept.trim()]
    }));
    setNewDept('');
  };

  const removeDepartment = (dept) => {
    setClientForm(prev => ({
      ...prev,
      departments: prev.departments.filter(d => d !== dept)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl lg:text-4xl font-black tracking-tight">
            Clients & Applications
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage client organizations and their applications
          </p>
        </div>
        
        <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 rounded-sm" data-testid="add-client-btn">
              <Plus className="h-4 w-4" />
              Add Client
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Create New Client</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Name *</label>
                  <Input
                    value={clientForm.name}
                    onChange={(e) => setClientForm(prev => ({ ...prev, name: e.target.value }))}
                    className="mt-1 rounded-sm"
                    placeholder="Client name"
                    data-testid="client-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Code *</label>
                  <Input
                    value={clientForm.code}
                    onChange={(e) => setClientForm(prev => ({ ...prev, code: e.target.value.toUpperCase().slice(0, 5) }))}
                    className="mt-1 rounded-sm font-mono"
                    placeholder="IST"
                    data-testid="client-code-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Used for ticket IDs (e.g., IST-001)</p>
                </div>
              </div>
              
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Logo URL</label>
                <Input
                  value={clientForm.logo_url}
                  onChange={(e) => setClientForm(prev => ({ ...prev, logo_url: e.target.value }))}
                  className="mt-1 rounded-sm"
                  placeholder="https://..."
                />
              </div>
              
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Departments</label>
                <div className="flex flex-wrap gap-2 mt-2 mb-3">
                  {clientForm.departments.map((dept) => (
                    <Badge key={dept} variant="secondary" className="gap-1 rounded-sm">
                      {dept}
                      <button onClick={() => removeDepartment(dept)} className="ml-1 hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newDept}
                    onChange={(e) => setNewDept(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addDepartment())}
                    className="rounded-sm"
                    placeholder="Add department"
                  />
                  <Button type="button" variant="outline" onClick={addDepartment} className="rounded-sm">
                    Add
                  </Button>
                </div>
              </div>
              
              <Button onClick={handleCreateClient} className="w-full rounded-sm" data-testid="create-client-btn">
                Create Client
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Clients List */}
        <div className="space-y-2">
          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider px-2">
            Clients ({clients.length})
          </h3>
          {clients.map((client) => (
            <button
              key={client.id}
              onClick={() => setSelectedClient(client)}
              className={`w-full text-left p-4 border rounded-sm transition-all ${
                selectedClient?.id === client.id
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
              data-testid={`client-${client.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <span className="font-semibold">{client.name}</span>
                    <p className="text-xs text-muted-foreground font-mono">{client.code}</p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </button>
          ))}
        </div>

        {/* Client Details & Applications */}
        <div className="lg:col-span-3">
          {selectedClient ? (
            <div className="space-y-6">
              {/* Client Info */}
              <Card className="border rounded-sm p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-sm bg-primary/10 flex items-center justify-center">
                      <Building2 className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                      <h2 className="font-heading text-2xl font-bold">{selectedClient.name}</h2>
                      <p className="text-muted-foreground font-mono">{selectedClient.code}</p>
                    </div>
                  </div>
                  <Badge variant={selectedClient.is_active ? 'default' : 'secondary'} className="rounded-sm">
                    {selectedClient.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Departments</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedClient.departments?.map((dept) => (
                      <Badge key={dept} variant="outline" className="rounded-sm">
                        {dept}
                      </Badge>
                    ))}
                  </div>
                </div>
              </Card>

              {/* Applications */}
              <Card className="border rounded-sm overflow-hidden">
                <div className="p-4 border-b border-border flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-2">
                    <AppWindow className="h-4 w-4" />
                    Applications ({applications.length})
                  </h3>
                  <Dialog open={appDialogOpen} onOpenChange={setAppDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-2 rounded-sm" data-testid="add-app-btn">
                        <Plus className="h-4 w-4" />
                        Add Application
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-sm">
                      <DialogHeader>
                        <DialogTitle className="font-heading">Add Application to {selectedClient.name}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Name *</label>
                          <Input
                            value={appForm.name}
                            onChange={(e) => setAppForm(prev => ({ ...prev, name: e.target.value }))}
                            className="mt-1 rounded-sm"
                            placeholder="Application name"
                            data-testid="app-name-input"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Description</label>
                          <Input
                            value={appForm.description}
                            onChange={(e) => setAppForm(prev => ({ ...prev, description: e.target.value }))}
                            className="mt-1 rounded-sm"
                            placeholder="Brief description"
                            data-testid="app-desc-input"
                          />
                        </div>
                        <Button onClick={handleCreateApp} className="w-full rounded-sm" data-testid="create-app-btn">
                          Create Application
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                
                <div className="divide-y divide-border">
                  {applications.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      No applications yet. Add one to get started.
                    </div>
                  ) : (
                    applications.map((app) => (
                      <div key={app.id} className="p-4 flex items-center justify-between" data-testid={`app-${app.id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-sm bg-muted flex items-center justify-center">
                            <AppWindow className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="font-semibold">{app.name}</p>
                            <p className="text-sm text-muted-foreground">{app.description || 'No description'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteApp(app.id)}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                            data-testid={`delete-app-${app.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </div>
          ) : (
            <div className="border border-dashed rounded-sm p-12 text-center">
              <p className="text-muted-foreground">Select a client to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Clients;
